import React, { FC } from 'react';
import { Box, Checkbox, FormControlLabel, Grid, TextField } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { AnimalType } from '../../../../../models/animal';
import {
  DiseaseViewModel,
  FarmMedicalSupplyViewModel,
  NotificationRuleRegionType
} from '../../../../../models/injection';
import { AnimalTypeSelect, FarmDiseaseSelect, FarmMedicalSupplySelect } from '../../../../../elements';
import { DayOfWeek } from '../../../../../models/common';

export interface INumberTabProps {
  animalType: AnimalType;
  setAnimalType: React.Dispatch<React.SetStateAction<AnimalType>>;
  farmSupplyId: string;
  farmDiseaseId: string;
  setFarmSupplyId: React.Dispatch<React.SetStateAction<string>>;
  setFarmDiseaseId: React.Dispatch<React.SetStateAction<string>>;
  farmSupplies: FarmMedicalSupplyViewModel[];
  farmDiseases: DiseaseViewModel[];
  loadingMedSupp: boolean;
  loadingDiseases: boolean;
  isLactating?: boolean;
  isPregnant?: boolean;
  setIsLactating: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  setIsPregnant: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  minInjected: string;
  setMinInjected: React.Dispatch<React.SetStateAction<string>>;
  dayOfWeek: DayOfWeek;
  setDayOfWeek: (day: DayOfWeek) => void;
  weekNumber: string;
  setWeekNumber: (number: string) => void;
  notificationRuleRegionType: NotificationRuleRegionType;
}

const NumberRuleTab: FC<INumberTabProps> = (props) => {
  const { t } = useTranslation();

  const {
    animalType,
    setAnimalType,
    farmSupplyId,
    farmDiseaseId,
    setFarmSupplyId,
    setFarmDiseaseId,
    farmSupplies,
    farmDiseases,
    loadingMedSupp,
    loadingDiseases,
    isPregnant,
    isLactating,
    setIsLactating,
    setIsPregnant,
    minInjected,
    setMinInjected,
    notificationRuleRegionType
  } = props;

  return (
    <Box p={1}>
      <Grid container spacing={1} alignItems="center">
        <Grid item container spacing={1} alignItems="center">
          <Grid item xs={12} sm={12} md={6}>
            <AnimalTypeSelect fullWidth value={animalType} onAnimalTypeChange={(a) => setAnimalType(a)} allowAny />
          </Grid>

          {notificationRuleRegionType === NotificationRuleRegionType.Injection && (
            <Grid item xs={12} sm={12} md={6}>
              <FarmMedicalSupplySelect
                fullWidth
                disabled={loadingMedSupp}
                supplies={farmSupplies}
                value={farmSupplyId}
                onSupplyChange={setFarmSupplyId}
              />
            </Grid>
          )}

          {notificationRuleRegionType === NotificationRuleRegionType.Mortality && (
            <Grid item xs={12} sm={12} md={6}>
              <FarmDiseaseSelect
                fullWidth
                disabled={loadingDiseases}
                diseases={farmDiseases}
                value={farmDiseaseId}
                onSupplyChange={setFarmDiseaseId}
              />
            </Grid>
          )}
        </Grid>

        <Grid item container spacing={1} alignItems="center">
          <Grid item xs={12} sm={12} md={6}>
            <TextField
              label={t('f-supp-rule-anim-value')}
              variant="outlined"
              fullWidth
              size="small"
              type="number"
              value={minInjected}
              onChange={(e) => setMinInjected(e.target.value)}
            />
          </Grid>
          {(animalType === AnimalType.Sow || animalType === AnimalType.Gilt) && (
            <>
              <Grid item xs={6} sm={6} md={3}>
                <FormControlLabel
                  label={t('f-animal-preg-only')}
                  control={
                    <Checkbox
                      color="primary"
                      checked={isPregnant || false}
                      onChange={(e) => setIsPregnant(e.target.checked)}
                      name="checkedF"
                    />
                  }
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <FormControlLabel
                  label={t('f-animal-is-lact')}
                  control={
                    <Checkbox
                      color="primary"
                      checked={isLactating || false}
                      onChange={(e) => setIsLactating(e.target.checked)}
                    />
                  }
                />
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default NumberRuleTab;
